/**
 * deckungsbeitragMappers (#1781) — narrow the generic
 * ``DerivedFact.fact_value: Record<string, unknown>`` returned by
 * twinService into the typed ``DeckungsbeitragRow``, and the flat
 * crop_cycle entity row into the display-join shape. Mirrors the
 * defensive style of ``liquiditaetMappers.ts``: one malformed row must
 * never crash the page — coerce numbers, default optionals, keep
 * nullables honest (a garbage ``db1_per_ha`` becomes null, NOT a false
 * 0 €/ha that would rank mid-table).
 */
import type { DerivedFact } from '../../../services/twinService';
import type {
  AllocationEntry,
  D2PoolEntry,
  PooledHaAllocation,
  UnallocatedPool,
  CostCoverage,
  CropCycleInfo,
  DeckungsbeitragRow,
  DirectCosts,
  EstablishmentBasis,
  EstablishmentDetail,
  GeometryProvenance,
  GeometryProvenanceEntry,
  InputPostenBasis,
  InputPostenBlock,
  InputPostenItem,
  InputRefusal,
  KuehlungEstimate,
  LabourPosten,
  LabourPostenItem,
  LabourPostenKind,
  LabourUnbooked,
  LabourUpliftGap,
  InvestitionBlock,
  InvestitionClass,
  UnvaluedReason,
  InvestitionComponents,
  InvestitionUnallocated,
  LandEstimate,
  SubstratEstimate,
  LeistungEstimate,
  LeistungPostenBlock,
  LeistungPostenItem,
  PerBasisImplausible,
  PerBasisImplausibleBand,
  PerBasisImplausibleBreach,
  PostenBasis,
  ProductInconsistency,
  ProductInconsistencyReason,
  RevenueProduct,
  SaatgutEstimate,
  SaatgutInput,
  SaatgutStand,
  StandProvenance,
  StandState,
  ValuationRefusal,
  VarietyDivergence,
  VerpackungMaterial,
} from './types';
import { D2_POOL_SLOTS, ESTABLISHMENT_BASES, INPUT_POSTEN_BASES, INPUT_REFUSALS, POSTEN_BASES, STAND_PROVENANCES, STAND_STATES, VALUATION_REFUSALS } from './types';

const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);

/**
 * Nullable display string. A blank string collapses to null (a blank
 * crop_year buckets as "Ohne Jahr"); a finite number stringifies (a
 * crop_year that arrived as ``2026`` instead of ``"2026"``).
 */
const strOrNull = (v: unknown): string | null => {
  if (typeof v === 'string' && v.trim() !== '') return v;
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return null;
};

/** Coerce to a finite number; ``fallback`` (default 0) when unusable. */
const num = (v: unknown, fallback = 0): number => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : fallback;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  }
  return fallback;
};

/**
 * Nullable number that stays honest: null/undefined AND garbage both map
 * to null (never NaN, never a fabricated 0 — matters for ``db1_per_ha``,
 * where 0 is a real mid-table rank and null means "unknown, sort last").
 */
const numOrNull = (v: unknown): number | null => {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
};

const toDirectCosts = (v: unknown): DirectCosts => {
  const src = v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  return {
    saatgut: num(src.saatgut),
    duenger: num(src.duenger),
    psm: num(src.psm),
    loehne: num(src.loehne),
    maschinen: num(src.maschinen),
    sonstige: num(src.sonstige),
    // HOF-518 — absent on every pre-HOF-518 fact, and ``num`` reads that as 0. Deliberate:
    // "this fact predates the land line" and "this farm books no land cost" are the same
    // thing for the drawer, which renders the pending row for both.
    land: num(src.land),
    // Labour-flow wave 2 — absent on every fact written before it, and ``num`` reads that
    // as 0 on the same reasoning as ``land`` above: the drawer renders the pending row for
    // both "this fact predates the labour lines" and "this farm records no labour".
    pflueck: num(src.pflueck),
    nebenkosten: num(src.nebenkosten),
    verpackung: num(src.verpackung),
    kuehlung: num(src.kuehlung),
    pflanzung: num(src.pflanzung),
    // Layer C — absent on every fact written before the Investition line; ``num`` reads that
    // as 0 on the ``land`` reasoning, and the drawer keeps the pending row unless the fact
    // also carries the ``investition`` block.
    investition: num(src.investition),
  };
};

/** The labour Posten kinds this app knows how to label. An unknown kind is dropped. */
const LABOUR_POSTEN_KINDS: ReadonlySet<string> = new Set([
  'picking',
  'insurance',
  'surcharge',
  'packing_labour',
  'packaging_tray',
  'packaging_crate',
  'packaging_pool_fee',
  'cooling',
  'planting',
  'field_care',
  'overhead',
  'employer_uplift',
  'employer_hour_cost',
  'pool_share',
  'fixed_share',
]);

const toLabourPostenItem = (v: unknown): LabourPostenItem | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const kind = strOrNull(o.kind);
  // A kind this build cannot LABEL must not reach the drawer as a blank row. This only
  // happens under deploy skew — a worker emitting a Posten kind the shipped frontend does
  // not know — and what it costs is the BREAKDOWN, never the figure: the line's euro comes
  // from `direct_costs`, so the farmer still reads the right number.
  //
  // Where the drop is visible depends on how much of the bucket went with it, and the
  // earlier comment here promised more than that (codex round 1). Drop SOME of a bucket's
  // items and the reconciliation tick reports the gap, but only once it clears
  // `MISMATCH_REPORT_SHARE` (5 % of the line) — below that the drawer stays quiet on
  // purpose, because a sub-5 % gap is rounding, not a defect. Drop ALL of them and the line
  // has no Posten at all, so `lineSelectability` disables it and there is no drawer to
  // report anything: the row reads as „kein Rechenweg", which is the honest fail-safe and
  // is why dropping beats rendering a blank row.
  if (!kind || !LABOUR_POSTEN_KINDS.has(kind)) return null;
  const amount = numOrNull(o.amount);
  if (amount === null) return null;
  return {
    month: strOrNull(o.month),
    kind: kind as LabourPostenKind,
    amount,
    hours: numOrNull(o.hours),
    kg: numOrNull(o.kg),
    worker_count: numOrNull(o.worker_count),
    share: numOrNull(o.share),
    basis: numOrNull(o.basis),
    pool: numOrNull(o.pool),
    // The material keys ride ONLY on a material item, so every labour item keeps the exact
    // shape it had — a hall share with `article: null` would be a claim about packaging.
    ...(kind.startsWith('packaging_')
      ? {
          article: strOrNull(o.article),
          article_label: strOrNull(o.article_label),
          pieces: numOrNull(o.pieces),
          unit_price: numOrNull(o.unit_price),
          price_year: numOrNull(o.price_year),
          price_carried: typeof o.price_carried === 'boolean' ? o.price_carried : null,
        }
      : {}),
  };
};

const toVerpackungMaterial = (v: unknown): VerpackungMaterial | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const objects = (list: unknown): Record<string, unknown>[] =>
    Array.isArray(list)
      ? list.filter((e): e is Record<string, unknown> => !!e && typeof e === 'object' && !Array.isArray(e))
      : [];
  const prices = objects(o.prices).flatMap((e) => {
    const article = strOrNull(e.article);
    const unit_price = numOrNull(e.unit_price);
    if (!article || unit_price === null) return [];
    return [
      {
        article,
        article_label: strOrNull(e.article_label),
        unit_price,
        year: numOrNull(e.year),
        carried: e.carried === true,
        pieces_bought: numOrNull(e.pieces_bought),
        documents: Array.isArray(e.documents) ? e.documents.filter((d): d is string => typeof d === 'string') : [],
      },
    ];
  });
  // An unpriced entry without a count says nothing a farmer can act on, so it is dropped.
  const unpriced = objects(o.unpriced).flatMap((e) => {
    const article = strOrNull(e.article);
    const pieces = numOrNull(e.pieces);
    if (!article || pieces === null || pieces <= 0) return [];
    return [{ article, article_label: strOrNull(e.article_label), pieces }];
  });
  return { prices, unpriced, gebinde_without_crate: numOrNull(o.gebinde_without_crate) };
};

const toLabourPosten = (v: unknown): LabourPosten | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const src = v as Record<string, unknown>;
  const out: LabourPosten = {};
  for (const [bucket, items] of Object.entries(src)) {
    if (!Array.isArray(items)) continue;
    const mapped = items.map(toLabourPostenItem).filter((i): i is LabourPostenItem => i !== null);
    if (mapped.length) out[bucket as keyof DirectCosts] = mapped;
  }
  return Object.keys(out).length ? out : null;
};

const toLabourUnbooked = (v: unknown): LabourUnbooked | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const by_class: Record<string, number> = {};
  const rawByClass = o.by_class;
  if (rawByClass && typeof rawByClass === 'object' && !Array.isArray(rawByClass)) {
    for (const [name, amount] of Object.entries(rawByClass as Record<string, unknown>)) {
      const value = numOrNull(amount);
      if (value !== null) by_class[name] = value;
    }
  }
  const unbooked: LabourUnbooked = { eur: num(o.eur), hours: num(o.hours), by_class };
  const unallocated = numOrNull(o.packing_unallocated);
  if (unallocated !== null && unallocated > 0) unbooked.packing_unallocated = unallocated;
  // Nothing unbooked and nothing unallocated is nothing to say. Returning the zeroed block
  // would put an empty "money we could not place" panel on every healthy cycle.
  if (unbooked.eur === 0 && unbooked.packing_unallocated === undefined) return null;
  return unbooked;
};

const toLabourUpliftGap = (v: unknown): LabourUpliftGap | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const gap: LabourUpliftGap = {
    records: num(o.records),
    hours: num(o.hours),
    wage_eur: num(o.wage_eur),
    pooled_wage_eur: num(o.pooled_wage_eur),
  };
  // Nothing missing is nothing to say: no empty panel on a healthy cycle.
  if (gap.wage_eur <= 0 && gap.pooled_wage_eur <= 0) return null;
  return gap;
};

const toKuehlungEstimate = (v: unknown): KuehlungEstimate | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const rate = numOrNull(o.rate);
  const quantity = numOrNull(o.quantity);
  const unit = strOrNull(o.unit);
  // Same shape of guard as ``toLandEstimate``: a rate or a quantity this app cannot render
  // honestly loses the Rechenweg, never the line and never the „geschätzt" flag.
  if (rate === null || rate <= 0) return null;
  if (quantity === null || quantity <= 0) return null;
  if (!unit) return null;
  return { rate, unit, quantity, source: strOrNull(o.source), tier: str(o.tier) };
};

/** One ``geometry_provenance`` sub-entry — keeps only a string source + finite params. */
const toProvEntry = (v: unknown): GeometryProvenanceEntry | undefined => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return undefined;
  const o = v as Record<string, unknown>;
  const entry: GeometryProvenanceEntry = { source: str(o.source) };
  // HOF-312 — the tier qualifies how firmly the source value is held (`farm_confirmed` for
  // a Reihenabstand recorded on the Schlag). Declared on the type and emitted on the fact,
  // so a mapper that dropped it would leave every RUNTIME row without it while a test that
  // builds the typed object by hand still passed. Empty string ⇒ omitted, like every other
  // optional param here.
  const tier = str(o.tier);
  if (tier !== '') entry.tier = tier;
  const rs = numOrNull(o.row_spacing_m);
  if (rs !== null) entry.row_spacing_m = rs;
  const pm = numOrNull(o.plants_per_m);
  if (pm !== null) entry.plants_per_m = pm;
  const pm2 = numOrNull(o.plants_per_m2);
  if (pm2 !== null) entry.plants_per_m2 = pm2;
  const tunnels = numOrNull(o.tunnels);
  if (tunnels !== null) entry.tunnels = Math.round(tunnels);
  const frl = numOrNull(o.field_row_length_m);
  if (frl !== null) entry.field_row_length_m = frl;
  return entry;
};

/**
 * The #2303 ``geometry_provenance`` block → a sanitised typed shape (or null when
 * absent/malformed). Defensive like the rest of the mapper: garbage params drop
 * out rather than surfacing "Reihenabstand NaN m" in the honesty line.
 */
export const toGeometryProvenance = (v: unknown): GeometryProvenance | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const prov: GeometryProvenance = {};
  const rl = toProvEntry(o.row_length_m);
  if (rl) prov.row_length_m = rl;
  const pc = toProvEntry(o.plant_count);
  if (pc) prov.plant_count = pc;
  if (o.comparability && typeof o.comparability === 'object' && !Array.isArray(o.comparability)) {
    const comparability: Record<string, string> = {};
    for (const [k, val] of Object.entries(o.comparability as Record<string, unknown>)) {
      if (typeof val === 'string') comparability[k] = val;
    }
    prov.comparability = comparability;
  }
  const se = o.substrate_eligibility;
  if (se && typeof se === 'object' && !Array.isArray(se)) {
    const e = se as Record<string, unknown>;
    const eligible = numOrNull(e.eligible);
    const total = numOrNull(e.total);
    const unknown = numOrNull(e.unknown);
    const fraction = numOrNull(e.fraction);
    if ((e.basis === 'metres' || e.basis === 'count') && eligible !== null && total !== null && unknown !== null && fraction !== null) {
      prov.substrate_eligibility = { basis: e.basis, eligible, total, unknown, fraction };
    }
  }
  return prov;
};

/**
 * #2442 — one persisted ``allocations`` audit entry → typed shape, or null when the
 * entry is unusable (a 0/garbage ``amount`` drops out, so Σ over the mapped rows can
 * never inflate the Gemeinkosten total with a phantom share). Nullable string fields
 * stay honest (a missing method/cost_center renders "—" in the breakdown, never
 * "undefined").
 */
const toAllocationEntry = (v: unknown): AllocationEntry | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const amount = numOrNull(o.amount);
  if (amount === null || amount === 0) return null;
  return {
    target_crop_cycle_id: strOrNull(o.target_crop_cycle_id),
    method: strOrNull(o.method),
    basis: numOrNull(o.basis),
    amount,
    cost_center_id: strOrNull(o.cost_center_id),
    source: strOrNull(o.source),
  };
};

/** The fact's ``allocations`` array → sanitised entries (garbage/0-share entries drop out). */
const toAllocations = (v: unknown): AllocationEntry[] => {
  if (!Array.isArray(v)) return [];
  const out: AllocationEntry[] = [];
  for (const entry of v) {
    const mapped = toAllocationEntry(entry);
    if (mapped) out.push(mapped);
  }
  return out;
};

/**
 * The fact's ``d2_pools`` array → the four D2 lines. ALWAYS four, in
 * ``D2_POOL_SLOTS`` order: an absent or partial payload is filled from the slot table, so
 * the drawer's line set is a property of the app and never of what the worker happened to
 * emit. A pool the fact does carry keeps its own figures and ``captured`` flag.
 */
const toD2Pools = (v: unknown): D2PoolEntry[] => {
  const bySlot = new Map<string, Record<string, unknown>>();
  if (Array.isArray(v)) {
    for (const entry of v) {
      if (entry && typeof entry === 'object') {
        const slot = (entry as Record<string, unknown>).slot;
        if (typeof slot === 'string') bySlot.set(slot, entry as Record<string, unknown>);
      }
    }
  }
  return D2_POOL_SLOTS.map(({ slot, label }) => {
    const raw = bySlot.get(slot);
    const amount = num(raw?.amount);
    return {
      slot,
      // the worker's label wins when it sent one (it owns the German), else the app's
      label: typeof raw?.label === 'string' && raw.label ? raw.label : label,
      amount,
      pool_total: num(raw?.pool_total),
      basis: numOrNull(raw?.basis),
      share: numOrNull(raw?.share),
      method: typeof raw?.method === 'string' ? raw.method : null,
      captured: raw?.captured === true,
    };
  });
};

/** One per-hectare pooled share (fuel/energy) → typed shape, or null when absent/unusable. */
const toPooledHaAllocation = (v: unknown): PooledHaAllocation | null => {
  if (!v || typeof v !== 'object') return null;
  const raw = v as Record<string, unknown>;
  const amount = numOrNull(raw.amount);
  // A share of zero is not a share: it would render a 0,00 € line claiming a derivation
  // the cycle did not take, which is the "measured zero" misread the material lines avoid.
  if (amount === null || amount <= 0) return null;
  return {
    amount,
    pot_total: num(raw.pot_total),
    ledger_category: typeof raw.ledger_category === 'string' ? raw.ledger_category : null,
    population: typeof raw.population === 'string' ? raw.population : null,
    method: typeof raw.method === 'string' ? raw.method : null,
    basis: numOrNull(raw.basis),
    share: numOrNull(raw.share),
    ...(raw.vehicle_split_pending === true ? { vehicle_split_pending: true } : {}),
    ...(raw.hall_power_unsplit === true ? { hall_power_unsplit: true } : {}),
  };
};

/** The fact's ``unallocated_pools`` → the visible residual (§II.3); garbage drops out. */
const toUnallocatedPools = (v: unknown): UnallocatedPool[] => {
  if (!Array.isArray(v)) return [];
  const out: UnallocatedPool[] = [];
  for (const entry of v) {
    if (!entry || typeof entry !== 'object') continue;
    const raw = entry as Record<string, unknown>;
    const amount = numOrNull(raw.amount);
    if (amount === null || amount <= 0) continue;
    if (typeof raw.kind !== 'string') continue;
    out.push({ kind: raw.kind, amount, reason: typeof raw.reason === 'string' ? raw.reason : '' });
  }
  return out;
};

/**
 * #2441/#2442 — the ``saatgut_estimate`` block → typed shape, or null when absent or
 * unusable (no basis, or a non-positive unit_price/quantity that couldn't value a
 * cost). Only a fully-formed, positively-valued estimate drives the formula breakdown.
 */
/**
 * #2487 Part B — one identified Pflanzgut input off the estimate's ``breakdown`` array →
 * typed shape, or null when unusable (a non-positive quantity/price drops out so a phantom
 * input never surfaces in the drawer). The gross ``value`` defaults to quantity×unit_price
 * when the fact omitted it (defence in depth — the materialiser always writes it).
 */
const toSaatgutInput = (v: unknown): SaatgutInput | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const quantity = numOrNull(o.quantity);
  const unit_price = numOrNull(o.unit_price);
  if (quantity === null || quantity <= 0 || unit_price === null || unit_price <= 0) return null;
  const value = numOrNull(o.value);
  const input: SaatgutInput = {
    variety: strOrNull(o.variety),
    product_id: strOrNull(o.product_id),
    quantity,
    unit_price,
    value: value !== null ? value : quantity * unit_price,
    provenance: strOrNull(o.provenance),
  };
  // #2538 — this input's own Preisspiegel line, both keys or neither (a half-formed pair
  // could only build a broken deep link, so it is dropped).
  applyPriceObservationRef(input, o);
  return input;
};

/**
 * #2538 — copy the ``catalog_item_id`` + ``observation_date`` pair from a raw fact block onto
 * a typed estimate/input, but ONLY when both are present and non-empty. A half-formed pair
 * cannot address a Preisspiegel line, so it is dropped and the consumer falls back to the app
 * root — key-absence stays honest all the way from the materialiser to the anchor.
 */
const applyPriceObservationRef = (
  target: { catalog_item_id?: string; observation_date?: string },
  raw: Record<string, unknown>,
): void => {
  const itemId = strOrNull(raw.catalog_item_id);
  const day = strOrNull(raw.observation_date);
  if (itemId && day) {
    target.catalog_item_id = itemId;
    target.observation_date = day;
  }
};

/** The estimate's ``breakdown`` array → sanitised identified inputs (garbage entries drop out). */
const toSaatgutBreakdown = (v: unknown): SaatgutInput[] => {
  if (!Array.isArray(v)) return [];
  const out: SaatgutInput[] = [];
  for (const entry of v) {
    const mapped = toSaatgutInput(entry);
    if (mapped) out.push(mapped);
  }
  return out;
};

/** #2593 — the mirrored worker vocabulary, as runtime guards (pinned against the Python source). */
const STAND_STATE_SET: ReadonlySet<string> = new Set<string>(STAND_STATES);
const STAND_PROVENANCE_SET: ReadonlySet<string> = new Set<string>(STAND_PROVENANCES);

/**
 * #2593 — a year / year-count off a window block, or null.
 *
 * ``numOrNull`` alone is not enough here: ``Number('')`` is 0, so a blank ``established_year``
 * would sail through as the year **0** and land in a sentence that explains a farmer's money
 * ("Anlage 0, seit 10 abgeschrieben"). Every such field is a whole year or a count of years,
 * so a blank or non-integer value is not a year at all — it degrades to null and the copy
 * degrades with it.
 *
 * #2608 — shared with the R5 ``establishment_detail`` block, whose year fields carry exactly
 * the same risk (renamed off ``standYear`` when the second consumer arrived, so the name does
 * not imply the #2533 stand register is the only source).
 */
const wholeYear = (v: unknown): number | null => {
  if (typeof v === 'string' && v.trim() === '') return null;
  const n = numOrNull(v);
  return n !== null && Number.isInteger(n) ? n : null;
};

/**
 * #2593 — the ``stand`` sub-block, or null when the emitted block cannot be interpreted.
 *
 * Key-absence honesty, applied at two different strengths because the two fields carry
 * different weight:
 *   - ``state`` is the VERDICT (it decides whether 0,00 € is a rule outcome or a data gap).
 *     An unrecognised token means this FE cannot tell which — so the whole block degrades to
 *     absent and the line falls back to the pre-#2593 story, never to a fabricated zero state.
 *   - ``provenance`` only says how much the Anlagejahr can be trusted. An unrecognised tier
 *     degrades to ``unknown`` — the LEAST-claiming value — rather than dropping a valid
 *     verdict or, worse, implying a farmer stated a year nobody stated.
 * Year fields are optional decoration: garbage becomes null (never NaN), and the copy degrades.
 */
const toSaatgutStand = (v: unknown): SaatgutStand | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const state = strOrNull(o.state);
  if (!state || !STAND_STATE_SET.has(state)) return null;
  const provenance = strOrNull(o.provenance);
  return {
    state: state as StandState,
    established_year: wholeYear(o.established_year),
    cleared_year: wholeYear(o.cleared_year),
    stand_life_years: wholeYear(o.stand_life_years),
    stand_year: wholeYear(o.stand_year),
    provenance: provenance && STAND_PROVENANCE_SET.has(provenance) ? (provenance as StandProvenance) : 'unknown',
  };
};

/**
 * #2695 E — the ``leistung_estimate`` block → typed shape, or null when the fact carries none
 * or carries one this app could not render HONESTLY.
 *
 * The producer ships every operand and BOTH their units precisely so the drawer renders the
 * derivation instead of re-deriving it, so the only job here is to refuse an incomplete block
 * rather than to patch one up. In particular:
 *
 *   - **neither unit is ever defaulted.** ``price_unit`` is €/t for most Marktfrüchte but €/dt
 *     for Kartoffeln and €/kg for berries, and ``quantity_unit`` is dt for arable harvest and
 *     kg for the berry pick stream — they vary INDEPENDENTLY. Substituting a default for a
 *     missing one would let the drawer print a confident Rechenweg in a unit nobody declared,
 *     which is the same 10x/100x defect class #2695 D closed one layer down (a review there
 *     caught exactly this, one layer out from the helper that had the contract right).
 *   - **``value`` is taken, never recomputed.** The FE must not re-multiply the operands: doing
 *     so means re-implementing the dt↔t conversion, and a UI that re-derives can disagree with
 *     the persisted DB1 the whole cascade is built on.
 */
/**
 * HOF-518 — the Landkosten operands. Refuses rather than patches, exactly like
 * ``toLeistungEstimate``: a block missing its unit or its rate can only be guessed at, and a
 * guessed land charge is the one thing this line exists to stop. The line keeps its € and
 * loses only its Rechenweg, which is the safe direction.
 */
const toLandEstimate = (v: unknown): LandEstimate | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const unit_price = numOrNull(o.unit_price);
  const area_ha = numOrNull(o.area_ha);
  const unit = strOrNull(o.unit);
  if (unit_price === null || unit_price <= 0) return null;
  if (area_ha === null || area_ha <= 0) return null;
  // The 10.000x guard, the config book's own trap said once more on the read side: a €/m²
  // rate rendered as €/ha is not a rounding error, it is four orders of magnitude.
  if (!unit) return null;
  const estimate: LandEstimate = {
    unit_price,
    unit,
    area_ha,
    basis: str(o.basis),
    source: str(o.source),
    estimate: o.estimate === true,
    catalog_item_id: str(o.catalog_item_id),
    observation_date: strOrNull(o.observation_date),
  };
  // Conditional, mirroring the fact: absent on every farm with one lease or none.
  if (o.contract_rates_ambiguous === true) estimate.contract_rates_ambiguous = true;
  return estimate;
};

/**
 * The Substrat operands (2026-10-06). Refuses rather than patches, exactly like
 * ``toLandEstimate``: a block missing its unit, its price or either quantity operand can only
 * be guessed at, and the row exists to say how a charge nobody invoiced was made. The Sonstige
 * line keeps its € and loses only the Substrat row; ``substrat_estimated`` keeps the label.
 */
export const toSubstratEstimate = (v: unknown): SubstratEstimate | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const unit_price = numOrNull(o.unit_price);
  const row_length_m = numOrNull(o.row_length_m);
  const litres_per_row_m = numOrNull(o.litres_per_row_m);
  const volume_m3 = numOrNull(o.volume_m3);
  const unit = strOrNull(o.unit);
  if (unit_price === null || unit_price <= 0) return null;
  if (row_length_m === null || row_length_m <= 0) return null;
  if (litres_per_row_m === null || litres_per_row_m <= 0) return null;
  if (volume_m3 === null || volume_m3 <= 0) return null;
  // The 1.000x guard: a € per litre rendered as € per m³.
  if (!unit) return null;
  const total_row_length_m = numOrNull(o.total_row_length_m);
  const grow_system_unknown = numOrNull(o.grow_system_unknown);
  return {
    unit_price,
    unit,
    row_length_m,
    ...(total_row_length_m !== null ? { total_row_length_m } : {}),
    ...(grow_system_unknown !== null ? { grow_system_unknown } : {}),
    ...(o.eligibility_basis === 'metres' || o.eligibility_basis === 'count' ? { eligibility_basis: o.eligibility_basis } : {}),
    row_length_source: strOrNull(o.row_length_source),
    litres_per_row_m,
    volume_m3,
    rule_source: strOrNull(o.rule_source),
    estimate: o.estimate === true,
    price_estimate: o.price_estimate === true,
    rule_estimated: o.rule_estimated === true,
    source: str(o.source),
    catalog_item_id: str(o.catalog_item_id),
    observation_date: strOrNull(o.observation_date),
  };
};

// ── Layer C „Investition" (2026-10-06) ─────────────────────────────────────────
const INVESTITION_COMPONENT_KEYS = ['afa', 'zinsen', 'reparatur', 'versicherung', 'reparatur_versicherung', 'nutzung'] as const;
const INVESTITION_METHODS: ReadonlySet<string> = new Set(['calculated', 'booked']);
const UNVALUED_REASONS: ReadonlySet<string> = new Set(['no_cost', 'invalid_operands', 'no_charge', 'leased_not_valued']);
const INVESTITION_KEYS: ReadonlySet<string> = new Set(['ha', 'lfm', 'pflanzen']);

/** Only the component keys this app can label, and only finite amounts. */
const toInvestitionComponents = (v: unknown): InvestitionComponents => {
  const out: InvestitionComponents = {};
  if (!v || typeof v !== 'object' || Array.isArray(v)) return out;
  const o = v as Record<string, unknown>;
  for (const key of INVESTITION_COMPONENT_KEYS) {
    const n = numOrNull(o[key]);
    if (n !== null) out[key] = n;
  }
  return out;
};

const strList = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x !== '') : []);

/**
 * One class, or null when this build cannot render it honestly: no label, or a method/key it
 * does not know (deploy skew). Dropping it costs the BREAKDOWN, never the figure — the line's
 * € comes from ``direct_costs.investition``, and the panel's Σ warning names the gap.
 */
export const toInvestitionClass = (v: unknown): InvestitionClass | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const id = strOrNull(o.id);
  const label = strOrNull(o.label);
  const method = str(o.method);
  const key = str(o.key);
  if (!id || !label || !INVESTITION_METHODS.has(method) || !INVESTITION_KEYS.has(key)) return null;
  const cls: InvestitionClass = {
    id,
    label,
    method: method as InvestitionClass['method'],
    key: key as InvestitionClass['key'],
    key_value: numOrNull(o.key_value),
    components: toInvestitionComponents(o.components),
    total: num(o.total),
    estimated: o.estimated === true,
    tier: str(o.tier),
    source: strOrNull(o.source),
  };
  if (o.refused === 'no_key' || o.refused === 'no_population') cls.refused = o.refused;
  for (const k of ['capex_per_unit', 'useful_life_years', 'interest_rate', 'repair_insurance_rate', 'pool_total', 'population_key_total', 'share'] as const) {
    const n = numOrNull(o[k]);
    if (n !== null) cls[k] = n;
  }
  if (o.per_unit && typeof o.per_unit === 'object' && !Array.isArray(o.per_unit)) {
    const pu = o.per_unit as Record<string, unknown>;
    const afa = numOrNull(pu.afa);
    const zinsen = numOrNull(pu.zinsen);
    const rv = numOrNull(pu.reparatur_versicherung);
    // All three or none: a partial per-unit sum would print a €/ha/Jahr that is not the rate.
    if (afa !== null && zinsen !== null && rv !== null) cls.per_unit = { afa, zinsen, reparatur_versicherung: rv };
  }
  if (o.pool && typeof o.pool === 'object' && !Array.isArray(o.pool)) cls.pool = toInvestitionComponents(o.pool);
  if (o.applies_to && typeof o.applies_to === 'object' && !Array.isArray(o.applies_to)) {
    const a = o.applies_to as Record<string, unknown>;
    cls.applies_to = { crops: strList(a.crops), cultivation_systems: strList(a.cultivation_systems) };
  }
  // ── asset rows: every key is conditional (a use-charge row omits the owned-only operands) ──
  if (o.asset && typeof o.asset === 'object' && !Array.isArray(o.asset)) {
    const a = o.asset as Record<string, unknown>;
    const entity_id = strOrNull(a.entity_id);
    if (entity_id) {
      cls.asset = {
        entity_type: str(a.entity_type),
        entity_id,
        name: strOrNull(a.name),
        number: numOrNull(a.number),
        field_id: strOrNull(a.field_id),
      };
    }
  }
  if (o.ownership === 'owned' || o.ownership === 'use_charge' || o.ownership === 'leased') cls.ownership = o.ownership;
  if (typeof o.paid_off === 'boolean') cls.paid_off = o.paid_off;
  if (Array.isArray(o.defaulted)) cls.defaulted = strList(o.defaulted);
  if (typeof o.capex_from_template === 'boolean') cls.capex_from_template = o.capex_from_template;
  for (const k of ['acquisition_cost_eur', 'repair_rate', 'insurance_rate', 'use_charge_eur_per_year'] as const) {
    const n = numOrNull(o[k]);
    if (n !== null) cls[k] = n;
  }
  for (const k of ['foil_cost_eur', 'foil_cycle_years'] as const) {
    if (k in o) cls[k] = numOrNull(o[k]);
  }
  for (const k of ['acquired_at', 'retired_at'] as const) {
    if (k in o) cls[k] = strOrNull(o[k]);
  }
  return cls;
};

const toInvestitionUnallocated = (v: unknown): InvestitionUnallocated | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const label = strOrNull(o.label);
  const total = numOrNull(o.total);
  if (!label || total === null) return null;
  return {
    id: str(o.id),
    label,
    components: toInvestitionComponents(o.components),
    total,
    source: strOrNull(o.source),
    reason: o.reason === 'no_population' ? 'no_population' : 'not_split',
  };
};

/**
 * The Investition block. Refused whole only when it has no numeric total — the one figure
 * it exists to explain; a malformed class or pool is dropped on its own.
 */
const toInvestitionBlock = (v: unknown): InvestitionBlock | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const total = numOrNull(o.total);
  if (total === null) return null;
  const block: InvestitionBlock = {
    total,
    estimated: o.estimated === true,
    classes: (Array.isArray(o.classes) ? o.classes : []).map(toInvestitionClass).filter((c): c is InvestitionClass => c !== null),
    unallocated: (Array.isArray(o.unallocated) ? o.unallocated : [])
      .map(toInvestitionUnallocated)
      .filter((u): u is InvestitionUnallocated => u !== null),
  };
  // Present only when a tunnel is — an absent key stays absent (zero-tunnel byte identity).
  if (Array.isArray(o.suppressed_classes)) {
    const sc: NonNullable<InvestitionBlock['suppressed_classes']> = [];
    for (const e of o.suppressed_classes) {
      if (!e || typeof e !== 'object' || Array.isArray(e)) continue;
      const r = e as Record<string, unknown>;
      const id = strOrNull(r.id);
      const label = strOrNull(r.label);
      const replaced_by = strOrNull(r.replaced_by);
      if (id && label && replaced_by) sc.push({ id, label, replaced_by });
    }
    block.suppressed_classes = sc;
  }
  if (Array.isArray(o.unvalued)) {
    const uv: NonNullable<InvestitionBlock['unvalued']> = [];
    for (const e of o.unvalued) {
      if (!e || typeof e !== 'object' || Array.isArray(e)) continue;
      const r = e as Record<string, unknown>;
      const a = r.asset;
      if (!a || typeof a !== 'object' || Array.isArray(a)) continue;
      const ao = a as Record<string, unknown>;
      const entity_id = strOrNull(ao.entity_id);
      if (!entity_id || !UNVALUED_REASONS.has(str(r.reason))) continue;
      uv.push({
        asset: { entity_type: str(ao.entity_type), entity_id, name: strOrNull(ao.name) },
        reason: r.reason as UnvaluedReason,
      });
    }
    block.unvalued = uv;
  }
  return block;
};

const toLeistungEstimate = (v: unknown): LeistungEstimate | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const value = numOrNull(o.value);
  const unit_price = numOrNull(o.unit_price);
  const quantity = numOrNull(o.quantity);
  const price_unit = strOrNull(o.price_unit);
  const quantity_unit = strOrNull(o.quantity_unit);
  if (value === null || value <= 0) return null;
  if (unit_price === null || unit_price <= 0) return null;
  if (quantity === null || quantity <= 0) return null;
  // The 100x guard: a block missing either unit cannot be rendered, only guessed at.
  if (!price_unit || !quantity_unit) return null;
  const estimate: LeistungEstimate = {
    value,
    unit_price,
    price_unit,
    quantity,
    quantity_unit,
    source: str(o.source),
  };
  // #2538 — the exact Preisspiegel line the book price is published under; both keys or neither.
  applyPriceObservationRef(estimate, o);
  // The book row's own justification prose. Conditional: an anchor-less row adds no null key.
  const anchor = strOrNull(o.anchor);
  if (anchor) estimate.anchor = anchor;
  return estimate;
};

const REFUSAL_SET = new Set<string>(VALUATION_REFUSALS);
const POSTEN_BASIS_SET = new Set<string>(POSTEN_BASES);

/**
 * HOF-102 — one ``leistung_posten.items[]`` entry → typed Posten, or null.
 *
 * Refuses rather than patches, exactly like ``toLeistungEstimate``:
 *
 *   - **``amount`` is taken, never recomputed.** Re-multiplying ``quantity x unit_price``
 *     here would re-implement the dt-t conversion the worker owns and could disagree with
 *     the DB1 the block's own cascade was computed from.
 *   - **neither unit is defaulted.** Same 10x/100x class as the estimate block above.
 *   - **an unknown ``reason`` or ``basis`` drops the row.** A token this app has no copy
 *     for would reach the farmer verbatim (``no_catalog_item``), which is worse than a
 *     Posten missing from a panel that already declares its Σ.
 *
 * A REFUSED Posten contributes 0 € by construction (HOF-102 D2), so ``amount`` is only
 * rejected as non-finite, never as non-positive. A VALUED Posten with no positive € is a
 * contradiction and is dropped.
 */
const toLeistungPostenItem = (v: unknown): LeistungPostenItem | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const operation_id = str(o.operation_id);
  const amount = numOrNull(o.amount);
  const booked_amount = numOrNull(o.booked_amount);
  if (!operation_id || amount === null || booked_amount === null) return null;
  const valued = o.valued === true;
  if (valued && amount <= 0) return null;
  const basis = str(o.basis);
  if (!POSTEN_BASIS_SET.has(basis)) return null;
  const reason = strOrNull(o.reason);
  if (valued ? reason !== null : !(reason && REFUSAL_SET.has(reason))) return null;
  const posten: LeistungPostenItem = {
    operation_id,
    month: strOrNull(o.month),
    amount,
    booked_amount,
    valued,
    basis: basis as PostenBasis,
    reason: valued ? null : (reason as ValuationRefusal),
    quantity: numOrNull(o.quantity),
    quantity_unit: strOrNull(o.quantity_unit),
  };
  const unit_price = numOrNull(o.unit_price);
  const price_unit = strOrNull(o.price_unit);
  // Both or neither: a price without its unit cannot be rendered, only guessed at.
  if (unit_price !== null && unit_price > 0 && price_unit) {
    posten.unit_price = unit_price;
    posten.price_unit = price_unit;
  }
  const catalog_item_id = strOrNull(o.catalog_item_id);
  if (catalog_item_id) posten.catalog_item_id = catalog_item_id;
  const price_month = strOrNull(o.price_month);
  if (price_month) posten.price_month = price_month;
  const carried_from = strOrNull(o.carried_from);
  if (carried_from) posten.carried_from = carried_from;
  const observation_count = numOrNull(o.observation_count);
  if (observation_count !== null) posten.observation_count = observation_count;
  const counterparty_count = numOrNull(o.counterparty_count);
  if (counterparty_count !== null) posten.counterparty_count = counterparty_count;
  const source = strOrNull(o.source);
  if (source) posten.source = source;
  return posten;
};

/**
 * HOF-102 — the ``leistung_posten`` block → typed shape, or null.
 *
 * **The reconciliation gate HOF-21 put here is gone, and deliberately so.** It compared
 * the block's OWN cascade against its Posten and rejected the whole block on a mismatch,
 * because a bad block would otherwise have been swapped into the page by the switcher.
 * There is no second cascade to swap in: this block is per-Posten DETAIL beside a
 * ``leistung`` that was computed without it. Rejecting it on an arithmetic disagreement
 * would not protect a € — it would delete the only explanation the drawer has of a number
 * it is going to show anyway. The panel's own Σ check (``sourceBreakdown.reconciles``) is
 * where a disagreement belongs: it says "Σ stimmt nicht" beside the figures, which is the
 * honest outcome, rather than silently showing no Einzelposten at all.
 *
 * What IS still refused is a block that cannot be rendered honestly, item by item — an
 * unknown refusal reason or basis token would otherwise reach a farmer verbatim as
 * ``no_catalog_item``. That rejection is whole-block rather than per-item: dropping one
 * Posten from a list the panel then sums would put a Σ on screen that is short by exactly
 * the row nobody can see.
 */
const toLeistungPostenBlock = (v: unknown): LeistungPostenBlock | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  if (!Array.isArray(o.items)) return null;
  const items = o.items.map(toLeistungPostenItem).filter((p): p is LeistungPostenItem => p !== null);
  if (items.length === 0 || items.length !== o.items.length) return null;
  return {
    basis: str(o.basis),
    leistung_booked: num(o.leistung_booked),
    valued_count: num(o.valued_count),
    refused_count: num(o.refused_count),
    basis_unknown: o.basis_unknown === true,
    items,
  };
};

const toSaatgutEstimate = (v: unknown): SaatgutEstimate | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const basis = strOrNull(o.basis);
  const unit_price = numOrNull(o.unit_price);
  const quantity = numOrNull(o.quantity);
  if (!basis || unit_price === null || unit_price <= 0 || quantity === null || quantity <= 0) return null;
  const estimate: SaatgutEstimate = { basis, unit_price, quantity, source: str(o.source) };
  // #2461/#2608 — the treatment axis, carried per token rather than per key-presence:
  //   - ``establishment`` carries its Standjahre and is REFUSED without a usable one. An
  //     amortisation claim whose divisor the FE cannot see would let the Rechenweg print a
  //     lifetime factor over a € it could not reproduce, so it degrades to key-absence.
  //   - ``annual`` (#2608) carries the token ALONE. There is no stand life to carry, and
  //     accepting a lifetime here would invite a divisor onto a line that books its gross
  //     whole — the calculation must be identical to a pre-#2608 fact's.
  //   - anything else (incl. the legacy ``amortise``, which the worker normalises away)
  //     drops out: an unknown token means this FE cannot tell which story is true.
  const cost_treatment = strOrNull(o.cost_treatment);
  const years = numOrNull(o.amortise_lifetime_years);
  if (cost_treatment === 'establishment' && years !== null && years > 0) {
    estimate.cost_treatment = 'establishment';
    estimate.amortise_lifetime_years = years;
  } else if (cost_treatment === 'annual') {
    estimate.cost_treatment = 'annual';
  }
  // #2487 Part B — the per-input product breakdown; added only when the leg was product-backed,
  // so a #2441 geometry estimate's block stays the pre-Part-B shape (key-absence honest).
  const breakdown = toSaatgutBreakdown(o.breakdown);
  if (breakdown.length > 0) estimate.breakdown = breakdown;
  // #2538 — the item/day the aggregate unit_price was published under; absent on an
  // unaddressable price-book entry and on a blended multi-lot aggregate.
  applyPriceObservationRef(estimate, o);
  // #2593 — the stand verdict that EXPLAINS the booked €; added only when the fact carries an
  // interpretable block, so an annual (or pre-#2533-A) estimate keeps its earlier shape.
  const stand = toSaatgutStand(o.stand);
  if (stand) estimate.stand = stand;
  return estimate;
};

/** #2608 — the mirrored worker R5 basis vocabulary, as a runtime guard. */
const ESTABLISHMENT_BASIS_SET: ReadonlySet<string> = new Set<string>(ESTABLISHMENT_BASES);

/**
 * #2608 — the R5 ``establishment_detail`` window, or null when the fact carries none / an
 * uninterpretable one.
 *
 * ``basis`` is the VERDICT (it decides whether the € is a share of a window or a full charge
 * with no window at all), so an unrecognised token degrades the WHOLE block to absent and the
 * "Etablierung (anteilig)" line falls back to its pre-#2608 bare €. Degrading to a default
 * basis would be worse than saying nothing: it would tell a farmer his cost was spread over
 * years it was not, or charged whole when it was not.
 */
const toEstablishmentDetail = (v: unknown): EstablishmentDetail | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const basis = strOrNull(o.basis);
  if (!basis || !ESTABLISHMENT_BASIS_SET.has(basis)) return null;
  return {
    basis: basis as EstablishmentBasis,
    planted_at: strOrNull(o.planted_at),
    planted_year: wholeYear(o.planted_year),
    end_year: wholeYear(o.end_year),
    lifetime_years: wholeYear(o.lifetime_years),
    stand_year: wholeYear(o.stand_year),
  };
};

/** The known #2487 divergence classes — an unknown token drops out (forward-compatible). */
const PRODUCT_INCONSISTENCY_REASONS: ReadonlySet<string> = new Set<ProductInconsistencyReason>([
  'crop_mismatch',
  'resale_mixed',
  'revenue_without_yield',
  'variety_mismatch',
]);

/** One ``revenue_products`` entry → typed shape, or null when it has no product name. */
const toRevenueProduct = (v: unknown): RevenueProduct | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const product = strOrNull(o.product);
  if (!product) return null;
  return { product, crop: strOrNull(o.crop), origin: strOrNull(o.origin) };
};

/**
 * #2487 Part C — the ``product_inconsistency`` block → typed shape, or null when absent or
 * unusable (no known reason token survives). Defensive: unknown reason tokens and malformed
 * revenue-product entries drop out, and a signal with NO surviving reason collapses to null
 * (never an empty banner). Reasons are deduped + sorted so the note reads stably.
 */
const toProductInconsistency = (v: unknown): ProductInconsistency | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const reasons = Array.isArray(o.reasons)
    ? [...new Set(o.reasons.filter((r): r is ProductInconsistencyReason => PRODUCT_INCONSISTENCY_REASONS.has(r as string)))].sort()
    : [];
  if (reasons.length === 0) return null;
  const revenue_products: RevenueProduct[] = Array.isArray(o.revenue_products)
    ? o.revenue_products.map(toRevenueProduct).filter((p): p is RevenueProduct => p !== null)
    : [];
  const signal: ProductInconsistency = { field_crop: strOrNull(o.field_crop), reasons, revenue_products };
  // #2487 Part E — the two disagreeing Sorten; added only when the block is actually present
  // AND the variety_mismatch reason survived, so a pre-Part-E signal keeps its exact shape.
  const varieties = toVarietyDivergence(o.varieties);
  if (varieties && reasons.includes('variety_mismatch')) signal.varieties = varieties;
  return signal;
};

/**
 * #2487 Part E — the ``varieties`` block → typed shape, or null when absent/malformed. Needs at
 * least one named Sorte to be worth rendering (a block with neither side named says nothing).
 */
const toVarietyDivergence = (v: unknown): VarietyDivergence | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const field_variety = strOrNull(o.field_variety);
  const external_variety = strOrNull(o.external_variety);
  if (!field_variety && !external_variety) return null;
  return { field_variety, external_variety, external_source: strOrNull(o.external_source) };
};

/**
 * #2773 B — the #2773 E ``per_basis_implausible`` block. The KEY's presence is the worker's
 * verdict, so a present-but-malformed block degrades to an all-null detail object rather
 * than dropping out: losing the Rechenweg is acceptable, losing the flag is not. Only
 * ``undefined``/``null``/``false`` (conditional-key absence shapes) mean "not flagged".
 */
const toPerBasisBreach = (o: Record<string, unknown>): PerBasisImplausibleBreach => {
  let band: PerBasisImplausibleBand | null = null;
  if (o.band && typeof o.band === 'object' && !Array.isArray(o.band)) {
    const b = o.band as Record<string, unknown>;
    band = { min: numOrNull(b.min), max: numOrNull(b.max) };
  }
  return { basis: strOrNull(o.basis), value: numOrNull(o.value), band };
};

const toPerBasisImplausible = (v: unknown): PerBasisImplausible | null => {
  if (v === undefined || v === null || v === false) return null;
  const o = v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  const block: PerBasisImplausible = { ...toPerBasisBreach(o), band_key: strOrNull(o.band_key) };
  // ``also`` — the second basis, when it breached too (2026-10-06). The same degrade as the
  // block itself, one level down: an OBJECT entry is a breach the worker named, so it is
  // kept even with garbage detail (the unit re-judgement reads null detail as undecidable
  // and leaves the badge on); a non-object entry names nothing and is dropped. The key is
  // set only when something survives, so a single-basis block keeps its four-key shape.
  if (Array.isArray(o.also)) {
    const also = o.also
      .filter((e): e is Record<string, unknown> => !!e && typeof e === 'object' && !Array.isArray(e))
      .map(toPerBasisBreach);
    if (also.length > 0) block.also = also;
  }
  return block;
};

/** #2769 — the ``cost_coverage`` evidence block, or null when absent/garbage. Fields null-degrade. */
const toCostCoverage = (v: unknown): CostCoverage | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  return {
    ops_total: numOrNull(o.ops_total),
    ops_costed: numOrNull(o.ops_costed),
    ratio: numOrNull(o.ratio),
    empty_buckets: Array.isArray(o.empty_buckets)
      ? o.empty_buckets.filter((b): b is string => typeof b === 'string')
      : [],
  };
};

const INPUT_BASIS_SET: ReadonlySet<string> = new Set(INPUT_POSTEN_BASES);
const INPUT_REFUSAL_SET: ReadonlySet<string> = new Set(INPUT_REFUSALS);

/**
 * HOF-307 — one ``duenger_posten`` / ``psm_posten`` item → typed Posten, or null. The same
 * refusals as ``toLeistungPostenItem`` and for the same reasons: ``amount`` is taken, never
 * re-multiplied; both halves of a price or neither; an unknown basis or reason drops the row
 * rather than reaching a farmer as a bare token. A refused input contributes 0 € by
 * construction, so ``amount`` is rejected only as non-finite; a VALUED input with no positive
 * € is a contradiction and is dropped.
 */
const toInputPostenItem = (v: unknown): InputPostenItem | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const operation_id = str(o.operation_id);
  const article = str(o.article);
  const amount = numOrNull(o.amount);
  if (!operation_id || !article || amount === null) return null;
  const valued = o.valued === true;
  if (valued && amount <= 0) return null;
  const basis = str(o.basis);
  if (!INPUT_BASIS_SET.has(basis)) return null;
  const reason = strOrNull(o.reason);
  if (valued ? reason !== null : !(reason && INPUT_REFUSAL_SET.has(reason))) return null;
  const posten: InputPostenItem = {
    operation_id,
    article,
    product_id: strOrNull(o.product_id),
    kategorie_id: strOrNull(o.kategorie_id),
    amount,
    valued,
    basis: basis as InputPostenBasis,
    reason: valued ? null : (reason as InputRefusal),
    estimate: o.estimate === true,
    quantity: numOrNull(o.quantity),
    quantity_unit: strOrNull(o.quantity_unit),
  };
  const unit_price = numOrNull(o.unit_price);
  const price_unit = strOrNull(o.price_unit);
  if (unit_price !== null && unit_price > 0 && price_unit) {
    posten.unit_price = unit_price;
    posten.price_unit = price_unit;
  }
  const price_year = numOrNull(o.price_year);
  if (price_year !== null && Number.isInteger(price_year)) posten.price_year = price_year;
  const price_month = strOrNull(o.price_month);
  if (price_month) posten.price_month = price_month;
  // The observation's OWN day, and the only field that can deep-link a curated estimate
  // (HOF-509, codex r2 P1). An estimate is dated by its ``as_of`` rather than by an
  // Erntejahr, so a link derived from ``price_year`` opens ``<year>-01-01`` — a cell the
  // tile does not carry. Dropping the field here made the link builder's preference for it
  // unreachable on real API data, which is the quietest way a fix can be inert.
  const price_date = strOrNull(o.price_date);
  if (price_date) posten.price_date = price_date;
  const carried_from = strOrNull(o.carried_from);
  if (carried_from) posten.carried_from = carried_from;
  const observation_count = numOrNull(o.observation_count);
  if (observation_count !== null) posten.observation_count = observation_count;
  const counterparty_count = numOrNull(o.counterparty_count);
  if (counterparty_count !== null) posten.counterparty_count = counterparty_count;
  const source = strOrNull(o.source);
  if (source) posten.source = source;
  return posten;
};

/**
 * HOF-307 — a ``duenger_posten`` / ``psm_posten`` block → typed shape, or null. Whole-block
 * rejection on one unrenderable item, exactly like ``toLeistungPostenBlock``: a list the
 * panel then sums must not be short by the row nobody can see.
 */
export const toInputPostenBlock = (v: unknown): InputPostenBlock | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  if (!Array.isArray(o.items)) return null;
  const items = o.items.map(toInputPostenItem).filter((p): p is InputPostenItem => p !== null);
  if (items.length === 0 || items.length !== o.items.length) return null;
  return {
    basis: str(o.basis),
    valued_count: num(o.valued_count),
    refused_count: num(o.refused_count),
    estimated_count: num(o.estimated_count),
    kauf_read_ok: o.kauf_read_ok !== false,
    items,
  };
};

/** ``fact_type: "deckungsbeitrag"`` → one typed row. ``entity_id`` is the crop_cycle id. */
export function toDeckungsbeitragRow(fact: DerivedFact): DeckungsbeitragRow {
  const fv =
    fact.fact_value && typeof fact.fact_value === 'object'
      ? (fact.fact_value as Record<string, unknown>)
      : ({} as Record<string, unknown>);
  // #2695 E — hoisted because the LABELLING flag is derived from BOTH signals. The fact's own
  // flag is authoritative when it is set (a block this app rejected must still be labelled —
  // losing the Rechenweg is an acceptable degrade, losing the warning is not), and a usable
  // block flags the row on its own, so an estimate can never reach the screen unlabelled.
  const leistung_estimate = toLeistungEstimate(fv.leistung_estimate);
  const land_estimate = toLandEstimate(fv.land_estimate);
  const substrat_estimate = toSubstratEstimate(fv.substrat_estimate);
  const investition = toInvestitionBlock(fv.investition);
  // Labour-flow wave 2 — same two-signal rule as the two blocks above: the fact's own
  // flag is authoritative when set, and a usable block flags the row on its own, so a
  // cooling estimate can never reach the screen unlabelled.
  const kuehlung_estimate = toKuehlungEstimate(fv.kuehlung_estimate);
  // HOF-102 — the Einzelposten behind ``leistung``. Read ONLY from ``leistung_posten``:
  // a pre-HOF-102 fact's ``leistung_valuation`` block was computed under rules this app
  // no longer implements (booked revenue as an input, a parallel cascade), so rendering
  // its Posten beside today's ``leistung`` would put two different calculations on one
  // line. A stale fact loses its breakdown until the re-materialise, which is the safe
  // direction: no Einzelposten beats wrong ones.
  const leistung_posten = toLeistungPostenBlock(fv.leistung_posten);
  const row: DeckungsbeitragRow = {
    derived_fact_id: fact.derived_fact_id,
    crop_cycle_id: fact.entity_id,
    period: strOrNull(fv.period),
    crop: str(fv.crop),
    field_id: strOrNull(fv.field_id),
    business_area_id: strOrNull(fv.business_area_id),
    area_ha: numOrNull(fv.area_ha),
    // #2303 — geometry Bezugsgröße denominators; absent on pre-#2303 / no-geometry
    // facts → null (that basis is simply not offered), never a fabricated 0.
    row_length_m: numOrNull(fv.row_length_m),
    plant_count: numOrNull(fv.plant_count),
    geometry_provenance: toGeometryProvenance(fv.geometry_provenance),
    leistung: num(fv.leistung),
    yield: num(fv.yield),
    // #2168 — fact-only; a pre-#2168 fact has no yield_unit → null (the FE renders it as "kg").
    yield_unit: strOrNull(fv.yield_unit),
    direktzahlung: num(fv.direktzahlung),
    direct_costs: toDirectCosts(fv.direct_costs),
    establishment_amortised: num(fv.establishment_amortised),
    // #2608 — the R5 window behind that €; null on a pre-#2608 fact and on any cycle with no
    // amortised establishment cost (the key is conditionally emitted).
    establishment_detail: toEstablishmentDetail(fv.establishment_detail),
    allocated_overhead: num(fv.allocated_overhead),
    dkfl: num(fv.dkfl),
    db1: num(fv.db1),
    db2: num(fv.db2),
    db1_per_ha: numOrNull(fv.db1_per_ha),
    // A row that doesn't SAY it is complete is treated as incomplete — the
    // honesty badge shows rather than overstating data quality.
    incomplete_on_shared_overhead: fv.incomplete_on_shared_overhead !== false,
    source: fv.source === 'operations' ? 'operations' : 'category_fallback',
    source_line_count: num(fv.source_line_count),
    allocations: toAllocations(fv.allocations),
    d2_pools: toD2Pools(fv.d2_pools),
    fuel_allocation: toPooledHaAllocation(fv.fuel_allocation),
    energy_allocation: toPooledHaAllocation(fv.energy_allocation),
    unallocated_pools: toUnallocatedPools(fv.unallocated_pools),
    saatgut_estimate: toSaatgutEstimate(fv.saatgut_estimate),
    leistung_estimated: fv.leistung_estimated === true || leistung_estimate !== null,
    leistung_estimate,
    leistung_posten,
    // HOF-518 — the fact's own flag OR a usable block, the ``leistung_estimated`` discipline
    // and for its reason: a block this app rejected must still carry its „geschätzt" label,
    // because a curated rate presented as the farm's contract rate is the under-labelling
    // this line exists to prevent.
    land_estimated: fv.land_estimated === true || land_estimate?.estimate === true,
    land_estimate,
    // The Substrat charge — the ``land_estimated`` rule and for its reason: the fact's flag
    // OR a usable estimate block, so a rejected block never strips the label.
    substrat_estimated: fv.substrat_estimated === true || substrat_estimate?.estimate === true,
    substrat_estimate,
    // Conditional, mirroring the fact: absent unless the engine set it.
    ...(fv.substrat_price_read_incomplete === true ? { substrat_price_read_incomplete: true } : {}),
    // Layer C — the ``land_estimated`` rule: the fact's flag OR a usable block's, so a
    // rejected block never strips the label. Every key is conditional on the fact.
    investition_estimated: fv.investition_estimated === true || investition?.estimated === true,
    investition,
    ...(fv.investition_read_incomplete === true ? { investition_read_incomplete: true } : {}),
    labour_observed: fv.labour_observed === true,
    labour_posten: toLabourPosten(fv.labour_posten),
    labour_unbooked: toLabourUnbooked(fv.labour_unbooked),
    labour_uplift_gap: toLabourUpliftGap(fv.labour_uplift_gap),
    verpackung_material: toVerpackungMaterial(fv.verpackung_material),
    kuehlung_estimated: fv.kuehlung_estimated === true || kuehlung_estimate?.tier === 'template',
    kuehlung_estimate,
    leistung_nach_pfluecken: num(fv.leistung_nach_pfluecken),
    // The fact's own flags, never derived from the block — same discipline as
    // ``leistung_estimated`` and for the same reason: a block this app rejected must
    // still carry its warning. Under-labelling a Leistung that is knowingly short is the
    // failure D2 exists to prevent.
    leistung_incomplete: fv.leistung_incomplete === true,
    leistung_unpriced_posten: num(fv.leistung_unpriced_posten),
    leistung_basis_unknown: fv.leistung_basis_unknown === true,
    leistung_deductions: num(fv.leistung_deductions),
    // HOF-307 — the material-input Posten and their flags, the ``leistung_*`` discipline:
    // the block is read only from its own key, and the flags come off the fact rather than
    // being derived from a block this app may have rejected.
    duenger_posten: toInputPostenBlock(fv.duenger_posten),
    psm_posten: toInputPostenBlock(fv.psm_posten),
    // HOF-519 — the machinery passes, read exactly like the two material blocks.
    maschinen_posten: toInputPostenBlock(fv.maschinen_posten),
    duenger_incomplete: fv.duenger_incomplete === true,
    psm_incomplete: fv.psm_incomplete === true,
    maschinen_incomplete: fv.maschinen_incomplete === true,
    duenger_unpriced_posten: num(fv.duenger_unpriced_posten),
    psm_unpriced_posten: num(fv.psm_unpriced_posten),
    maschinen_unpriced_posten: num(fv.maschinen_unpriced_posten),
    duenger_estimated: fv.duenger_estimated === true,
    psm_estimated: fv.psm_estimated === true,
    maschinen_estimated: fv.maschinen_estimated === true,
    product_inconsistency: toProductInconsistency(fv.product_inconsistency),
    // HOF-286 — the Sorte the fact was PRICED at, when it carries one. Until this issue no
    // fact did, and the field was documented as entity-joined-only; it is fact-carried now
    // because the resolution belongs beside the € it drove (``crop_cycle.variety``, else
    // FruitForecast's ``variety_external`` where the two do not conflict). ``withCropCycle``
    // below fills it from the cycle when the fact is silent, so every pre-HOF-286 row is
    // byte-identical.
    variety: strOrNull(fv.variety),
    // #2574 — entity-joined below (``withCropCycle``), UNLIKE ``variety``: the stored fact
    // carries its own ``period``, never the crop_cycle's crop_year.
    cycle_crop_year: null,
    // HOF-195 — entity-joined below (``withHauptschlag``) off the ``field`` entity, for the
    // same reason: it is a grouping attribute of the Schlag, not a figure of the fact.
    hauptschlag: null,
  };
  // #2773 B — worker-stamped row-quality keys (producers: #2773 D / #2773 E / #2769 b).
  // Attached CONDITIONALLY so an unflagged row keeps the exact pre-#2773 shape (key-absence
  // == not flagged, mirroring the facts' own conditional-key discipline).
  if (fv.not_marketable === true) row.not_marketable = true;
  const perBasisImplausible = toPerBasisImplausible(fv.per_basis_implausible);
  if (perBasisImplausible) row.per_basis_implausible = perBasisImplausible;
  const costCoverage = toCostCoverage(fv.cost_coverage);
  // The #2695 E hoist: the fact's own flag is authoritative when set (a malformed evidence
  // block loses the Rechenweg, never the warning), and a usable block flags the row on its
  // own, so under-coverage can never reach the screen unlabelled.
  if (fv.cost_coverage_low === true || costCoverage !== null) {
    row.cost_coverage_low = true;
    if (costCoverage) row.cost_coverage = costCoverage;
  }
  return row;
}

/** Flat crop_cycle entity row (attributes merged with entity_id) → display-join shape. */
export function toCropCycleInfo(entity: Record<string, unknown>): CropCycleInfo {
  return {
    crop_cycle_id: str(entity.crop_cycle_id) || str(entity.entity_id),
    crop: strOrNull(entity.crop),
    variety: strOrNull(entity.variety),
    area_ha: numOrNull(entity.area_ha),
    field_id: strOrNull(entity.field_id),
    business_area_id: strOrNull(entity.business_area_id),
    crop_year: strOrNull(entity.crop_year),
  };
}

/**
 * Display-join a fact row with its crop_cycle. Fact-carried figures always
 * win — in particular ``area_ha``/``db1_per_ha`` stay the fact-time values
 * the margin was computed on (backfilling today's entity area under a
 * stored per-ha figure would misstate it). The cycle only contributes
 * display fields the fact lacks (``variety``, ``crop``, ``field_id``,
 * ``business_area_id``) when the fact left them blank.
 *
 * HOF-286 moved ``variety`` under that same rule. It used to be assigned unconditionally,
 * which was right while no fact carried one and wrong the moment one did: the worker now
 * stamps the Sorte its Pflanzgut price resolution used, and the cycle's own ``variety`` is
 * MyFarm24-owned and empty on exactly the rows this fixes (WF's Brombeeren read plain
 * „Brombeeren" while the twin knew „Sweet Royalla" on ``variety_external`` all along).
 *
 * #2574 — it also threads the cycle's OWN ``crop_year`` across as ``cycle_crop_year``. That
 * one is NOT a display field and never falls back to the fact's ``period``: the whole point
 * is to be able to tell the two apart (own-year fact vs #1720 thin per-year fact), so an
 * unjoined cycle must leave it null rather than fabricate a match.
 */
/**
 * HOF-195 — the ``field``-entity display join: the row's Hauptschlag, off the very
 * ``field`` rows ``useDeckungsbeitrag`` already fetches to build ``fieldNames``. Zero new
 * network calls, which is the whole reason the rollup is read-time.
 *
 * Applied AFTER ``withCropCycle``, never before: a fact need not carry ``field_id`` at all
 * — the cycle supplies it — so joining first would silently miss every fact that gets its
 * Schlag from its crop_cycle. An unresolved id and an entity without the attribute both
 * yield null, and a null Hauptschlag can never join an analysis unit.
 */
export function withHauptschlag(
  row: DeckungsbeitragRow,
  hauptschlagByFieldId: Record<string, string>,
): DeckungsbeitragRow {
  const hauptschlag = row.field_id ? hauptschlagByFieldId[row.field_id] ?? null : null;
  return hauptschlag === row.hauptschlag ? row : { ...row, hauptschlag };
}

export function withCropCycle(
  row: DeckungsbeitragRow,
  cycle: CropCycleInfo | undefined,
): DeckungsbeitragRow {
  if (!cycle) return row;
  return {
    ...row,
    crop: row.crop || (cycle.crop ?? ''),
    variety: row.variety ?? cycle.variety,
    field_id: row.field_id ?? cycle.field_id,
    business_area_id: row.business_area_id ?? cycle.business_area_id,
    cycle_crop_year: cycle.crop_year,
  };
}
